import { useState } from 'react';

import config from '@/config';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import PopupToBottomSheet from '@/shared-components/popups/components/PopupToBottomSheet';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import './styles/AddShippingAddress.css';

function AddShippingAddressContent({ addressData, onAdd, previewMode, editMode, close }) {
  const t = useTranslations('common.shared.add_shipping_address');
  const [formState, setFormState] = useState(Utils.initShippingAddressForm(addressData));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submitAddress(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editMode) {
        const { response, data } = await UserService.editShippingAddress(formState);

        if (response.ok) {
          setLoading(false);
          onAdd(data);
          close();
        } else if (response.status === 400) {
          setLoading(false);
          setError({ element: data.error_data.element, error_code: data.error_type });
        } else {
          setLoading(false);
          setError({ element: 'address_name', error_code: 'SHIPPING_ERROR' });
        }
      } else {
        const { response, data } = await UserService.addShippingAddress(formState);

        if (response.ok) {
          setLoading(false);
          onAdd(data);
          close();
        } else if (response.status === 400) {
          setLoading(false);
          setError({ element: data.error_data.element, error_code: data.error_type });
        } else {
          setLoading(false);
          setError({ element: 'address_name', error_code: 'SHIPPING_ERROR' });
        }
      }
    } catch (err) {
      console.error('submitAddress error', err);
      setLoading(false);
      setError({ element: 'address_name', error_code: 'SHIPPING_ERROR' });
    }
  }

  function updateFormState(key, value) {
    setFormState(Utils.addAttributeToObject(key, value, formState));
  }

  return (
    <>
      <form onSubmit={submitAddress} className="add-shipping-address__form remove-scrollbar">
        <div className="add-shipping-address__header">
          <Text className="add-shipping-address__header-text">
            {
              editMode ? 
                t('titles.edit')
                : previewMode ?
                  t('titles.collector_preview')
                  :
                  t('titles.add')
            }
          </Text>

          <XIcon className="add-shipping-address__close" onClick={close}/>
        </div>

        <div className="add-shipping-address__group">
          <PublicFormInput 
            value={formState.address_name} 
            onChange={e=>updateFormState("address_name", e.target.value)}
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.name')}
            element="address_name" 
          />

          <PublicFormInput 
            value={formState.address_surname} 
            onChange={e=>updateFormState("address_surname", e.target.value)}
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.surname')}
            element="address_surname"
          />
        </div>

        <PublicFormInput 
          value={formState.address_street} 
          onChange={e=>updateFormState("address_street", e.target.value)}
          readOnly={loading || previewMode} 
          error={error} 
          placeholder={t('placeholders.address')}
          element="address_street" 
          className="add-shipping-address__input-full new-input-container"
        />

        <div className="add-shipping-address__group">
          <PublicFormInput 
            value={formState.address_city} 
            onChange={e=>updateFormState("address_city", e.target.value)} 
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.city')}
            element="address_city" 
          />

          <PublicFormInput 
            value={formState.address_zip_code} 
            onChange={e=>updateFormState("address_zip_code", e.target.value)}
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.zip_code')}
            element="address_zip_code" 
          />
        </div>

        <div className="add-shipping-address__group">
          <PublicFormInput 
            value={formState.address_region} 
            onChange={e=>updateFormState("address_region", e.target.value)} 
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.province_region')}
            element="address_region" 
          />

          <PublicFormInput 
            value={formState.address_country} 
            onChange={value=>updateFormState("address_country", value)}
            className="add-shipping-address__group-item new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.country')}
            element="address_country" 
            type="select" 
            selectOptions={{ options: config.forms.prefix_options, type: "country" }}
          />
        </div>

        <div className="add-shipping-address__group add-shipping-address__group--phone">
          <PublicFormInput 
            value={formState.address_phone.address_phone_prefix} 
            onChange={value=>updateFormState("address_phone.address_phone_prefix", value)}
            className="add-shipping-address__phone-prefix new-input-container" 
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.phone_prefix')}
            element="address_phone.address_phone_prefix" 
            type="select" 
            selectOptions={{ options: config.forms.prefix_options, type: "phone_prefix" }}
          />

          <PublicFormInput 
            value={formState.address_phone.address_phone_number} 
            onChange={e=>updateFormState("address_phone.address_phone_number", e.target.value)}
            className="add-shipping-address__phone-number new-input-container"
            readOnly={loading || previewMode} 
            error={error} 
            placeholder={t('placeholders.phone_number')}
            element="address_phone.address_phone_number" 
          />
        </div>

        {!previewMode && (
          <ContinueButton className="add-shipping-address__button font-size-medium" loading={loading}>
            {
              editMode ? 
                t('buttons.save_changes')
                : 
                t('buttons.add_shipping_address')
            }
          </ContinueButton>
        )}
      </form>
    </>
  );
}

export default function AddShippingAddress({ open, close, addressData, onAdd, previewMode=false, editMode=false }) {
  return (
    <>
      <PopupToBottomSheet 
        className="add-shipping-address__popup"
        open={open} 
        close={close} 
        forcePopup
        popupOptions={{ opacity: true }}
      >
        <AddShippingAddressContent 
          addressData={addressData}
          onAdd={onAdd}
          previewMode={previewMode}
          editMode={editMode} 
          close={close} 
        />
      </PopupToBottomSheet>
    </>
  );
}
